import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { moduleKeys } from '@entities/Module';
import type { Level, ModuleSummary } from '@shared/types';
import {
  fetchPublicModuleById,
  fetchPublicModules,
  forkPublicModule,
  type FetchPublicModulesArgs,
  type PublicModule,
  type PublicModulePage,
} from '../api/publicModuleApi';

export const publicModuleKeys = {
  all: ['public-modules'] as const,
  list: (args: FetchPublicModulesArgs) => ['public-modules', 'list', args] as const,
  byId: (id: string) => ['public-modules', id] as const,
};

export interface UsePublicModulesQueryArgs {
  page: number;
  pageSize?: number;
  search?: string;
  level?: Level | 'all';
  official?: boolean;
}

export function usePublicModulesQuery(args: UsePublicModulesQueryArgs) {
  return useQuery<PublicModulePage, Error>({
    queryKey: publicModuleKeys.list(args),
    queryFn: () => fetchPublicModules(args),
    staleTime: 60_000,
    // Поиск/уровень/страница: держим прошлую выдачу, пока грузится новая.
    placeholderData: keepPreviousData,
  });
}

export function usePublicModuleByIdQuery(id: string | null | undefined) {
  return useQuery<PublicModule | null, Error>({
    queryKey: publicModuleKeys.byId(id ?? ''),
    queryFn: () => fetchPublicModuleById(id!),
    enabled: !!id,
  });
}

export function useForkPublicModuleMutation() {
  const qc = useQueryClient();
  return useMutation<ModuleSummary, Error, string>({
    mutationFn: forkPublicModule,
    onSuccess: (created) => {
      qc.setQueryData<ModuleSummary[]>(['modules'], (old) => [...(old ?? []), created]);
      // «Добавлено» и число установок в каталоге и на странице модуля.
      void qc.invalidateQueries({ queryKey: publicModuleKeys.all });
      // Новый модуль — сразу в списках «Слов и модулей», в строке создания, в сводке «что повторить» и в словах
      // (владелец 2026-10-28: «добавил модуль из готовых, а он появился только после обновления страницы»).
      void qc.invalidateQueries({ queryKey: moduleKeys.listsRoot });
      void qc.invalidateQueries({ queryKey: ['sessions', 'overview'] });
      void qc.invalidateQueries({ queryKey: ['words'] });
    },
  });
}
